import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  getFacilityStatements,
  getFacilityStatement
} from '../../services/facilitySubscriptionService';
import { generateStatementPdf } from './StatementPdfGenerator';

const FacilityStatementsTab = ({ facilityId, facilityName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statements, setStatements] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [statementDetails, setStatementDetails] = useState({});
  const [downloading, setDownloading] = useState(null);

  // Load statements list
  const loadStatements = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getFacilityStatements(facilityId);
      setStatements(data || []);
    } catch (err) {
      console.error('Error loading statements:', err);
      setError('Failed to load billing statements');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    loadStatements();
  }, [loadStatements]);

  // Load statement details when expanded
  const handleToggleExpand = async (month) => {
    if (expandedMonth === month) {
      setExpandedMonth(null);
      return;
    }

    setExpandedMonth(month);

    // Load details if not already loaded
    if (!statementDetails[month]) {
      setLoadingDetails(month);
      try {
        const details = await getFacilityStatement(facilityId, month);
        setStatementDetails(prev => ({ ...prev, [month]: details }));
      } catch (err) {
        console.error('Error loading statement details:', err);
      } finally {
        setLoadingDetails(null);
      }
    }
  };

  // Download PDF
  const handleDownloadPdf = async (statement) => {
    setDownloading(statement.month);
    try {
      // Load full details if needed
      let details = statementDetails[statement.month];
      if (!details) {
        details = await getFacilityStatement(facilityId, statement.month);
        setStatementDetails(prev => ({ ...prev, [statement.month]: details }));
      }

      // Generate and download PDF
      await generateStatementPdf(details, { name: facilityName, id: facilityId });
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format month display
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading billing statements...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" />
            Monthly Billing Statements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and download monthly billing statements for this facility
          </Typography>
        </Box>
        <Tooltip title="Refresh statements">
          <IconButton onClick={loadStatements} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statements Table */}
      {statements.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No Statements Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly billing statements will appear here once generated
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ width: 50 }}></TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Discount</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Tax</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statements.map((statement) => (
                <React.Fragment key={statement.month}>
                  {/* Main Row */}
                  <TableRow 
                    hover 
                    sx={{ 
                      cursor: 'pointer',
                      bgcolor: expandedMonth === statement.month ? 'action.selected' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleExpand(statement.month)}
                      >
                        {expandedMonth === statement.month ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell onClick={() => handleToggleExpand(statement.month)}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatMonth(statement.month)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={() => handleToggleExpand(statement.month)}>
                      {formatCurrency(statement.subtotal)}
                    </TableCell>
                    <TableCell align="right" onClick={() => handleToggleExpand(statement.month)}>
                      <Typography variant="body2" color="success.main">
                        {statement.discountAmount > 0 ? `-${formatCurrency(statement.discountAmount)}` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={() => handleToggleExpand(statement.month)}>
                      {formatCurrency(statement.taxAmount)}
                    </TableCell>
                    <TableCell align="right" onClick={() => handleToggleExpand(statement.month)}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(statement.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={statement.status === 'finalized' ? 'Finalized' : 'Draft'}
                        size="small"
                        color={statement.status === 'finalized' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={downloading === statement.month ? <CircularProgress size={16} /> : <DownloadIcon />}
                        onClick={() => handleDownloadPdf(statement)}
                        disabled={downloading === statement.month}
                      >
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Details Row */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, bgcolor: 'grey.50' }}>
                      <Collapse in={expandedMonth === statement.month} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 3 }}>
                          {loadingDetails === statement.month ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : statementDetails[statement.month] ? (
                            <>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                Line Item Breakdown
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Quantity</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {statementDetails[statement.month]?.lineItems?.map((item, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{item.description}</TableCell>
                                      <TableCell align="center">{item.quantity}</TableCell>
                                      <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                      <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                                    </TableRow>
                                  ))}
                                  {(!statementDetails[statement.month]?.lineItems || 
                                    statementDetails[statement.month]?.lineItems.length === 0) && (
                                    <TableRow>
                                      <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                          No line items available
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>

                              {/* Totals Summary */}
                              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ width: 300 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Subtotal:</Typography>
                                    <Typography variant="body2">{formatCurrency(statement.subtotal)}</Typography>
                                  </Box>
                                  {statement.discountAmount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="body2" color="success.main">Discount:</Typography>
                                      <Typography variant="body2" color="success.main">
                                        -{formatCurrency(statement.discountAmount)}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Tax:</Typography>
                                    <Typography variant="body2">{formatCurrency(statement.taxAmount)}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Total:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(statement.totalAmount)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Provider Count Info */}
                              {statementDetails[statement.month]?.providerCount > 0 && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Provider Count: {statementDetails[statement.month]?.providerCount} | 
                                    Generated: {new Date(statementDetails[statement.month]?.generatedAt?.seconds * 1000 || statementDetails[statement.month]?.generatedAt).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Unable to load statement details
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Info Text */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Statements are generated at the end of each billing period. Click on a row to view line item details.
        </Typography>
      </Box>
    </Box>
  );
};

export default FacilityStatementsTab;
